import React, { useEffect, useState } from "react"
import cn from "classnames"
import * as Icons from "@nova/component-icons"
import { Dropdown } from "@nova/component-dropdown"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPanel } from "features/map/control/selectors"
import { setWeatherOpacity } from "features/map/control/slice"
import { WeatherTimelineControls } from "./WeatherTimelineControls"
import { WeatherLegend } from "./WeatherLegend"

import { useMapBackground, MapBackgroundViewType } from "features/map/control/useMapStyles"
import { Slider } from "components/Slider"
import { SelectWeatherMode, WEATHER_MODES } from "./SelectWeatherMode"
import { WeatherModeConfiguration } from "./WeatherModeConfiguration"
import { SelectPlaybackSpeed } from "./SelectPlaybackSpeed"
import {
    LightningStrikeDataModal,
    LIGHTNING_STRIKE_DATA_DONT_SHOW_KEY,
} from "../lightning/LightningStrikeDataModal"

type Props = { viewType: MapBackgroundViewType }

export const WeatherControls = ({ viewType }: Props) => {
    const dispatch = useAppDispatch()
    const { isLight } = useMapBackground(viewType)
    const panel = useAppSelector(selectWeatherPanel)
    const { mode } = panel
    const [strikeDataModalIsOpen, setStrikeDataModalIsOpen] = useState(false)

    useEffect(() => {
        if (mode !== "lightning") return
        if (localStorage.getItem(LIGHTNING_STRIKE_DATA_DONT_SHOW_KEY) === "true") return
        setStrikeDataModalIsOpen(true)
    }, [mode])

    return (
        <>
            {strikeDataModalIsOpen && (
                <LightningStrikeDataModal onClose={() => setStrikeDataModalIsOpen(false)} />
            )}
            <div className="absolute left-0 bottom-[calc(100%+8px)]">
                <WeatherLegend mode={mode} isLight={isLight} />
            </div>
            <div
                className={cn(
                    "relative grid grid-cols-[230px_1fr] gap-0 pointer-events-auto rounded-xl h-[124px]",
                    {
                        ["border border-white-300 bg-black-600 text-gray-0"]: !isLight,
                        ["border border-black-300 bg-white-500 text-gray-900"]: isLight,
                    }
                )}
            >
                <div className="w-full flex flex-col justify-center gap-2 px-5 py-2">
                    <div className="flex items-center justify-between gap-2">
                        <Dropdown>
                            <Dropdown.Trigger
                                as="button"
                                className="group flex w-fit items-center gap-2 bg-transparent text-left"
                            >
                                <span
                                    className={cn("text-base font-normal leading-[1.4]", {
                                        ["text-gray-900"]: isLight,
                                        ["text-gray-0"]: !isLight,
                                    })}
                                >
                                    {WEATHER_MODES.find((m) => m.value === mode)?.label ??
                                        "Weather"}
                                </span>
                                <Icons.ArrowDropUp className="text-gray-400 group-aria-expanded:hidden" />
                                <Icons.ArrowDropdown className="hidden text-gray-400 group-aria-expanded:block" />
                            </Dropdown.Trigger>
                            <Dropdown.Menu
                                unstyled
                                position="top-left"
                                className="w-[240px] rounded-xl bg-gray-900 p-3 shadow-lg"
                            >
                                <SelectWeatherMode />
                            </Dropdown.Menu>
                        </Dropdown>

                        <WeatherModeConfiguration isLight={isLight} />
                    </div>

                    <SelectPlaybackSpeed isLight={isLight} />
                    <div className="flex flex-row gap-2 items-center">
                        <span className="w-20 shrink-0 text-xs text-gray-400 leading-none whitespace-nowrap">
                            Opacity
                        </span>
                        <div className="flex-1 min-w-0">
                            <Slider
                                min={50}
                                max={100}
                                step={1}
                                value={Math.round(panel.opacity * 100)}
                                onChange={(value) => dispatch(setWeatherOpacity(value / 100))}
                                className="!max-w-none"
                                size="small"
                                isLightMode={isLight}
                            />
                        </div>
                    </div>
                </div>

                <div
                    className={cn("border-l flex items-center", {
                        ["border-gray-700"]: !isLight,
                        ["border-gray-100"]: isLight,
                    })}
                >
                    <WeatherTimelineControls isLight={isLight} />
                </div>
            </div>
        </>
    )
}
